import type { Location } from "react-router-dom";

/**
 * What a detail modal is drawn on top of, and what closing it goes back to.
 *
 * A modal route is still a route: `/opportunity/<slug>` is shareable, bookmarkable and
 * survives reload. What it is *over* is carried in history state rather than in the URL,
 * so the address bar names the record and nothing else — a link shares the opportunity,
 * not the filters the sharer happened to have set.
 *
 * `depth` counts modals opened within this session's navigation. It is what makes close
 * mean the same thing as Back: at depth 1 an opportunity opened from a client modal closes
 * back to that client modal; at depth 0 — a link opened cold, with no history to step into
 * — there is nothing behind, so close replaces the entry with a sensible surface instead.
 */
export type DetailBackground = {
  /** The location the modal is drawn over. */
  location: Location;
  depth: number;
};

export type DetailLocationState = { background?: DetailBackground } | null;

/** Where a cold-loaded modal sits when there is no history behind it. */
const COLD_BACKGROUND: Record<string, string> = {
  opportunity: "/",
  user: "/",
  client: "/clients",
};

/**
 * The background for a modal reached directly — a pasted link, a bookmark, a new tab.
 *
 * An opportunity or a person belongs to the pipeline; a client belongs to the registry.
 * Guessing wrong is cheap (one extra click) and guessing nothing is not: a modal over a
 * blank page has no context and closing it would have nowhere to go.
 */
export function coldBackgroundPath(pathname: string): string {
  const entity = pathname.split("/").filter(Boolean)[0] ?? "";
  return COLD_BACKGROUND[entity] ?? "/";
}

/**
 * The state to push when opening a detail from somewhere in the app.
 *
 * The *current* background is carried forward when one modal opens another, so the client
 * modal underneath keeps the surface it was itself drawn over. Without that, closing twice
 * from an opportunity opened inside a client would land nowhere.
 */
export function openDetailState(current: Location, state: DetailLocationState): DetailLocationState {
  const existing = state?.background;

  if (existing) {
    // Already inside a modal: keep the same underlying surface and go one deeper. Never
    // record a modal as a background — it is not a surface, and the route table has no
    // entry for it, so the background render would fall through to the catch-all.
    return { background: { location: existing.location, depth: existing.depth + 1 } };
  }

  if (isDetailPath(current.pathname)) {
    // A **cold-loaded** modal opening another: a pasted `/client/…` link, then a row in
    // its list. There is no background to inherit, and using the current location would
    // make a detail path the background — which the surface route table cannot render, so
    // the catch-all would redirect the real history to `/` and the chain would vanish.
    return {
      background: {
        location: { ...current, pathname: coldBackgroundPath(current.pathname), search: "", hash: "" },
        depth: 0,
      },
    };
  }

  return { background: { location: current, depth: 0 } };
}

/** Whether closing should step back through history rather than replace the entry. */
export function closesByGoingBack(state: DetailLocationState): boolean {
  const background = state?.background;
  // depth 0 with a background still means "opened from a surface in this session", so Back
  // is correct there too. Only a cold load — no background at all — has nothing behind it.
  return background !== undefined;
}

/** The routes that render as a modal over whatever the reader was already looking at. */
export const DETAIL_PATH_PREFIXES = ["/opportunity/", "/client/", "/user/"] as const;

export function isDetailPath(pathname: string): boolean {
  return DETAIL_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}
